/**
 * KNK-1.57 / FR-33a — the live team map: parsing, copy and the visibility rule.
 *
 * The server decides everything that matters (`live_team_map()`, migration 20261011a8): who is on
 * the map (in-session reps the caller reaches through field.live_view.team or .all, never the
 * caller), each rep's newest position and last Knock, and the state. App closed is "no update for
 * 5 minutes in an open, unpaused session", computed in the database so the web and the phone can
 * never disagree about it. joules-mobile `lib/liveTeamMap.ts` mirrors this file.
 *
 * ⚠️ Every call to the read LOGS A VIEW (polls within 2 minutes merge into one sitting), and the
 * rep reads who looked through `my_live_view_log()`. Nothing here is the gate or the log.
 */
import type { KnockModeStatus } from '@/lib/knockMode';

export type LiveState = 'active' | 'paused' | 'app_closed';

export interface LivePosition {
  lat: number;
  lng: number;
  accuracyM: number | null;
  recordedAt: string | null;
}

export interface LiveKnock {
  knockId: string | null;
  at: string | null;
  doorId: string | null;
  address: string | null;
  statusCode: string | null;
  category: string | null;
}

export interface LiveRep {
  repId: string;
  name: string;
  sessionId: string | null;
  sessionStartedAt: string | null;
  state: LiveState;
  pausedAt: string | null;
  lastSeenAt: string | null;
  lastSeenSeconds: number | null;
  position: LivePosition | null;
  lastKnock: LiveKnock | null;
}

export interface LiveTeamMap {
  asOf: string | null;
  appClosedAfterMinutes: number;
  reps: LiveRep[];
}

/** Often enough that a rep reads App closed within half a minute of the 5-minute mark. */
export const LIVE_MAP_REFRESH_MS = 30_000;

export const LIVE_MAP_COPY = {
  pageTitle: 'Live team map',
  pageSubtitle: 'Reps in a Knock Session right now, while the app is open on their phone.',
  nobody: 'Nobody on your team is in a session right now.',
  off: 'The live team map is for managers with live map access, in an account with Knock Mode on.',
  logged:
    'Every time you open this map it is logged, and each rep on it can see that you viewed their position.',
  notInSession: 'Reps who are not in a session never appear here.',
  noPosition: 'No position yet',
  noKnock: 'No Knock yet this session',
  noAddress: 'a Door without an address',
  appClosedHint: (minutes: number) =>
    `No update for ${minutes} minutes. The app is not open on their phone; that is not the same as not working.`,
  pausedSince: (when: string) => `Paused since ${clock(when)}`,
  inSessionSince: (when: string) => `In session since ${clock(when)}`,
  accuracy: (m: number) => `within ${Math.round(m)} m`,
  updated: (when: string) => `Updated ${clock(when)}`,
} as const;

const STATE_LABEL: Record<LiveState, string> = { active: 'Active', paused: 'Paused', app_closed: 'App closed' };
const STATES: readonly LiveState[] = ['active', 'paused', 'app_closed'];

export function stateLabel(s: LiveState): string {
  return STATE_LABEL[s];
}

function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

function parsePosition(v: unknown): LivePosition | null {
  if (!isRecord(v)) return null;
  const lat = num(v.lat);
  const lng = num(v.lng);
  if (lat === null || lng === null) return null;
  return { lat, lng, accuracyM: num(v.accuracy_m), recordedAt: str(v.recorded_at) };
}

function parseKnock(v: unknown): LiveKnock | null {
  if (!isRecord(v)) return null;
  return {
    knockId: str(v.knock_id),
    at: str(v.at),
    doorId: str(v.door_id),
    address: str(v.address),
    statusCode: str(v.status_code),
    category: str(v.disposition_category),
  };
}

export function parseLiveTeamMap(raw: unknown): LiveTeamMap {
  const o = isRecord(raw) ? raw : {};
  const list = Array.isArray(o.reps) ? o.reps : [];
  const reps: LiveRep[] = [];
  for (const item of list) {
    if (!isRecord(item)) continue;
    const repId = str(item.rep_id);
    if (!repId) continue;
    reps.push({
      repId,
      name: str(item.name) ?? 'Rep',
      sessionId: str(item.session_id),
      sessionStartedAt: str(item.session_started_at),
      // Never Active by default: an unreadable state must not claim someone is out working.
      state: STATES.includes(item.state as LiveState) ? (item.state as LiveState) : 'app_closed',
      pausedAt: str(item.paused_at),
      lastSeenAt: str(item.last_seen_at),
      lastSeenSeconds: num(item.last_seen_seconds),
      position: parsePosition(item.position),
      lastKnock: parseKnock(item.last_knock),
    });
  }
  return { asOf: str(o.as_of), appClosedAfterMinutes: num(o.app_closed_after_minutes) ?? 5, reps };
}

export function lastSeenLine(seconds: number | null): string {
  if (seconds === null) return 'Not seen yet';
  if (seconds < 60) return 'Seen just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Seen ${minutes} min ago`;
  return `Seen ${Math.floor(minutes / 60)} h ${minutes % 60} min ago`;
}

/** `dispositionLabel` is the Account's display name for the Knock's status code, when known. */
export function lastKnockLine(rep: LiveRep, dispositionLabel: string | null): string {
  const k = rep.lastKnock;
  if (!k) return LIVE_MAP_COPY.noKnock;
  const what = dispositionLabel ?? k.statusCode ?? 'Knock';
  const when = k.at ? clock(k.at) : '';
  return `Last Knock: ${what} at ${k.address ?? LIVE_MAP_COPY.noAddress}${when ? `, ${when}` : ''}`;
}

export function liveMapSummary(reps: LiveRep[]): string {
  if (reps.length === 0) return LIVE_MAP_COPY.nobody;
  const n = (s: LiveState) => reps.filter((r) => r.state === s).length;
  const parts = STATES.filter((s) => n(s) > 0).map((s) => `${n(s)} ${STATE_LABEL[s]}`);
  return `${reps.length} in a session: ${parts.join(', ')}`;
}

/**
 * Should the sidebar offer the map, and should the page render it? Knock Mode on AND a live view
 * rung (field.live_view.team / .all; a superadmin through system.override). Nothing while loading:
 * an entry that appears and vanishes has already told someone it exists. The database refuses
 * anyone else regardless.
 */
export function liveTeamMapVisible(input: { status: KnockModeStatus; loading: boolean; canView: boolean }): boolean {
  if (input.loading || !input.status.enabled) return false;
  return input.canView;
}

/** The server's "this is not for you": no live view rung (42501) or Knock Mode off (KM001). */
export function isLiveMapRefusal(e: unknown): boolean {
  const code = isRecord(e) ? e.code : undefined;
  return code === '42501' || code === 'KM001';
}

export const LIVE_MAP_PERMISSIONS = ['field.live_view.team', 'field.live_view.all', 'system.override'] as const;
